import type { RefObject } from 'react';
import { forwardRef, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { MenuRenderFn } from '@lexical/react/LexicalContextMenuPlugin';

import { PlusIcon } from '@polyai/ui/components/atoms/Icons';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import { cn } from '@polyai/ui/utils';

import { ReservedActions } from 'components/molecules/LexicalEditor/plugins/ActionPicker/hooks/usePickerOptions';
import type {
  OptionGroup,
  PickerOption,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import { useEventListener } from 'hooks/useEventListener';
import { ACTION_MENU_ID } from '../../constants';
import * as Styled from './Menu.styled';

export const MAX_HEIGHT = Styled.MAX_HEIGHT;
export const Z_INDEX = Styled.Z_INDEX;

type ActionMenuContentProps = {
  dataId: string;
  menuRenderProps: Parameters<MenuRenderFn<PickerOption>>[1];
  groups: OptionGroup[];
  style?: React.CSSProperties;
};

// this should be moved elsewhere but its not compatible with the useEventListener interface
// so gonna keep it here for now cuz it works
function useActiveElement(element?: RefObject<HTMLElement | null>) {
  const [activeElement, setActiveElement] = useState(
    () => document?.activeElement,
  );

  useEventListener(
    'focus',
    () => setActiveElement(document?.activeElement),
    element as any,
  );

  useEventListener('blur', () => setActiveElement(null), element as any);

  return { activeElement };
}

const ActionMenu = forwardRef<HTMLDivElement, ActionMenuContentProps>(
  (
    {
      menuRenderProps: {
        selectOptionAndCleanUp,
        setHighlightedIndex,
        selectedIndex,
        options,
      },
      groups,
      dataId,
      style,
    },
    ref,
  ) => {
    const [editor] = useLexicalComposerContext();
    const { activeElement: _activeElement } = useActiveElement({
      current: editor.getRootElement(),
    });
    const activeOptionKeys = options.map(o => o.key);

    return (
      <Styled.ActionMenuContainer
        ref={ref}
        aria-label="Action picker"
        data-rti-id={dataId}
        data-test-id="action-menu"
        id={ACTION_MENU_ID}
        role="menu"
        style={style}
        onClick={event => {
          // For preventing onBlur event from firing
          event.stopPropagation();
        }}
      >
        {groups.map(group => {
          if (!group.options?.some(o => activeOptionKeys.includes(o.key))) {
            return null;
          }

          const createAction = group.options?.find(
            o => o.id === ReservedActions.CREATE_ACTION,
          );

          const options = group.options.filter(o => o.type === 'option');

          return (
            <Styled.ActionGroup key={`action-group-${group.name}`}>
              <Styled.ActionGroupHeader>
                <Styled.GroupLabel>
                  {group.icon}
                  <Text variant="bodySmall">{group.name}</Text>
                </Styled.GroupLabel>
                {createAction && (
                  <Styled.CreateAction
                    key={createAction.key}
                    ref={createAction.setRefElement}
                    $isSelected={selectedIndex === createAction.index}
                    aria-label={`Add ${group.singularName}`}
                    data-test-id={`add-${group.singularName}-btn`}
                    tabIndex={-1}
                    onClick={event => {
                      event.preventDefault();
                      setHighlightedIndex(createAction.index);
                      selectOptionAndCleanUp(createAction);
                    }}
                    onMouseEnter={() => {
                      setHighlightedIndex(createAction.index);
                    }}
                  >
                    <PlusIcon color="iconBrand" />
                  </Styled.CreateAction>
                )}
              </Styled.ActionGroupHeader>
              <Styled.OptionsContainer role="menu">
                {options.map(option => {
                  if (!activeOptionKeys.includes(option.key)) {
                    return null;
                  }

                  return (
                    <Tooltip
                      key={option.key}
                      content={option.description}
                      portal
                      placement="right"
                      delay={{
                        open: 200,
                      }}
                    >
                      <Styled.Option
                        ref={option.setRefElement}
                        $isSelected={selectedIndex === option.index}
                        aria-label={option.name}
                        aria-selected={selectedIndex === option.index}
                        data-test-id={`${group.singularName}-${option.name}`}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={event => {
                          event.preventDefault();
                          setHighlightedIndex(option.index);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(option.index);
                        }}
                      >
                        {option.icon}
                        <Stack
                          as="span"
                          flex="1"
                          justify="space-between"
                          spacing="xs2"
                        >
                          <Text
                            as="span"
                            className={cn(option.icon && 'ml-xs2')}
                            variant="bodySemibold"
                          >
                            {option.name}
                          </Text>
                        </Stack>
                      </Styled.Option>
                    </Tooltip>
                  );
                })}
              </Styled.OptionsContainer>
            </Styled.ActionGroup>
          );
        })}
      </Styled.ActionMenuContainer>
    );
  },
);

export { ActionMenu };
